import React, { useRef, type ComponentType } from 'react';
import { Drawer, DrawerCloseButton, DrawerContent, DrawerSidebar } from '@atlaskit/drawer';
import CloseIcon from '@atlaskit/icon/core/migration/close--cross';
import { Grid, xcss } from '@atlaskit/primitives';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';

import Placeholder, { PlaceholderNew } from '@atlassian/jira-placeholder/src/index.tsx';
import { monitor } from '@atlassian/jira-portfolio-3-common/src/analytics/performance.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import {
	PACKAGE_NAME,
	ERROR_REPORTING_TEAM,
	PERFORMANCE_CREATE_PLAN_WIZARD_LOAD_TIME,
} from '../common/constants.tsx';
import { useCreateNewPlanWizardDrawer } from '../controllers/index.tsx';
import type { Props as PlanWizardProps } from './content/types.tsx';
import messages from './messages.tsx';
import { CreateNewPlanWizardDrawerOld } from './index-old.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncPlanWizard = lazy<ComponentType<PlanWizardProps>>(
	() => {
		monitor.startTask(PERFORMANCE_CREATE_PLAN_WIZARD_LOAD_TIME);
		return import(
			/* webpackChunkName: "async-portfolio-3-create-new-plan-wizard" */ './content'
		).then((module) => module.default);
	},
	{ ssr: false },
);

const AsyncPlanWizardWrapped = (props: PlanWizardProps) => (
	<PlaceholderNew name="async-plan-create-wizard" fallback={null}>
		<AsyncPlanWizard {...props} />
	</PlaceholderNew>
);

export type CreateNewPlanWizardDrawerProps = {
	PlanWizard?: ComponentType<PlanWizardProps>;
};

const CreateNewPlanWizardDrawer = ({
	PlanWizard = fg('concurrent-rendering-fix-plan-create-drawer')
		? AsyncPlanWizardWrapped
		: AsyncPlanWizard,
}: CreateNewPlanWizardDrawerProps) => {
	const [{ isOpen, plan }, { closeDrawer }] = useCreateNewPlanWizardDrawer();
	const keepMounted = useRef(false);

	const { formatMessage } = useIntl();

	if (!keepMounted.current && !isOpen) return null;
	// Keep mounted to Drawer close animation works
	if (!keepMounted.current) keepMounted.current = true;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleRequestClose = (e: any) => {
		if (e.key === 'Escape') {
			return;
		}
		closeDrawer();
	};

	// Wrapping the AsyncPlanWizard component directly in Placeholder results the Drawer
	// flickering when it is opened immediately after the /plans page loads - either through
	// the create-plan=true query param, or by quickly clicking the top RHS Create plan
	// button. Wrapping the parent Drawer in Placeholder resolves this issue.

	return (
		<Placeholder name="create-new-plan-wizard" fallback={null}>
			<Drawer
				isOpen={isOpen}
				width="full"
				onClose={handleRequestClose}
				label={formatMessage(messages.dialogLabel)}
			>
				<DrawerSidebar>
					<DrawerCloseButton
						icon={(iconsProps) => <CloseIcon {...iconsProps} LEGACY_size="medium" />}
					/>
				</DrawerSidebar>
				<DrawerContent>
					<Grid xcss={containerStylesCentred} alignItems="center">
						<ReportErrors
							id={PACKAGE_NAME}
							packageName={PACKAGE_NAME}
							teamName={ERROR_REPORTING_TEAM}
							sendToPrivacyUnsafeSplunk
						>
							<PlanWizard plan={plan} />
						</ReportErrors>
					</Grid>
				</DrawerContent>
			</Drawer>
		</Placeholder>
	);
};

export default componentWithFG(
	'finish_jira-portfolio-3-plan-wizard_migration',
	CreateNewPlanWizardDrawer,
	CreateNewPlanWizardDrawerOld,
);

const containerStylesCentred = xcss({
	minHeight: '100%',
	paddingTop: 'space.300',
	paddingBottom: 'space.300',
});
